//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import { useOtChainState } from '@react/redux/store'
import React, { ReactElement, useEffect } from 'react'

import ButtonModal from '@react/components/ButtonModal'
import { companyProfilesGet } from '@react/fetchers/companyProfileFetchers'
import { CompanyProfileCTO } from '@shared/CompanyProfileCTO'
import { logOT } from '@shared/loggingShared'
import CompanyProfileForm from './CompanyProfileForm'

function AdminCompanyProfileRow(props: {
  companyProfileCTO: CompanyProfileCTO
  companyProfiles: CompanyProfileCTO[]
  refreshCallBack: () => void
}): ReactElement {
  const { companyProfileCTO } = props
  const [isHovered, setIsHovered] = React.useState(false)

  useEffect(() => {
    window.addEventListener(
      'modalClose',
      () => {
        props.refreshCallBack()
      },
      false
    )

    return () => {
      window.removeEventListener(
        'modalClose',
        () => {
          props.refreshCallBack()
        },
        false
      )
    }
  })
  if (!companyProfileCTO) {
    return <></>
  }

  return (
    <TableRow
      key={companyProfileCTO.uuid}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        '&:hover': {
          backgroundColor: 'action.hover',
        },
        position: 'relative',
      }}
    >
      <TableCell key={'name'} align="left" sx={{ position: 'relative', overflow: 'visible' }}>
        <Box sx={{ position: 'relative', overflow: 'visible' }}>
          <Typography alignContent={'left'} variant="bodyBold" gutterBottom component="div">
            {companyProfileCTO.companyName}
          </Typography>
        </Box>
      </TableCell>
      <TableCell key={'email'} align="left">
        <Box display={'flex'} flexDirection={'column'}>
          {companyProfileCTO.emails.map((email, index) => (
            <Typography key={`email-${index}`} variant={'caption'}>
              {email}
            </Typography>
          ))}
        </Box>
      </TableCell>
      <TableCell key={'wallets'} align="left">
        <Box display={'flex'} flexDirection={'column'}>
          {companyProfileCTO.wallets.map((wallet, index) => (
            <Typography key={`email-${index}`} variant={'captionMuted'}>
              {wallet}
            </Typography>
          ))}
        </Box>
      </TableCell>
      <TableCell key={'uuid'} align="right">
        <Typography display={'inline'} variant={'caption'}>
          {companyProfileCTO.instructions}
        </Typography>
        {isHovered && (
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              transform: 'translateY(-50%)',
              zIndex: 5,
              overflow: 'visible',
            }}
          >
            <ButtonModal title={'Edit'} variant="primary">
              <CompanyProfileForm companyProfileCTO={companyProfileCTO} companyProfiles={props.companyProfiles} />
            </ButtonModal>
          </Box>
        )}
      </TableCell>
      <TableCell key={'nav'} align="right"></TableCell>
    </TableRow>
  )
}

export default function AdminCompanyProfileList(): ReactElement {
  const {
    stageConfiguration: { serverApiBaseUrl },
  } = useOtChainState()

  const [companyProfiles, setCompanyProfiles] = React.useState<CompanyProfileCTO[]>([])
  const theme = useTheme()

  const refreshCallBack = () => {
    fetchCompanyProfiles(serverApiBaseUrl)
  }
  const fetchCompanyProfiles = async (serverApiBaseUrl: string | undefined) => {
    try {
      if (serverApiBaseUrl) {
        const companyProfiles = await companyProfilesGet(serverApiBaseUrl)
        setCompanyProfiles(companyProfiles)
      }
    } catch (e) {
      logOT.error('fetchCompanyProfiles', e)
    }
  }
  useEffect(() => {
    window.addEventListener(
      'modalClose',
      () => {
        fetchCompanyProfiles(serverApiBaseUrl)
      },
      false
    )

    return () => {
      window.removeEventListener(
        'modalClose',
        () => {
          fetchCompanyProfiles(serverApiBaseUrl)
        },
        false
      )
    }
  })

  useEffect(() => {
    fetchCompanyProfiles(serverApiBaseUrl)
  }, [serverApiBaseUrl])

  logOT.debug('companyProfiles ', companyProfiles)

  if (companyProfiles.length === 0) {
    return (
      <>
        <Typography align="left" sx={{ fontWeight: 'bold' }} variant={'h4'}>
          Company Profiles
        </Typography>
        <ButtonModal title={'Add Company Profile'} variant="primary">
          <CompanyProfileForm companyProfiles={companyProfiles} />
        </ButtonModal>

        <Typography variant="h4" gutterBottom component="div">
          No Company Profiles
        </Typography>
      </>
    )
  }
  return (
    <Paper
      data-testid="AdminCompanyProfileList"
      sx={{
        marginTop: '20px',
        marginBottom: 4,
        marginRight: '2rem',
        marginLeft: '2rem',
        padding: '24px',
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography align="left" sx={{ display: 'inline', float: 'left', fontWeight: 'bold' }} variant={'h4'}>
          Company Profiles
        </Typography>
        <ButtonModal title={'Add Company Profile'} variant="primary">
          <CompanyProfileForm companyProfiles={companyProfiles} />
        </ButtonModal>
      </Box>

      <TableContainer sx={{ maxHeight: 800 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '20%' }} align="left">
                <Typography variant="bodyBold">Company</Typography>
              </TableCell>
              <TableCell style={{ width: '25%' }} align="left">
                <Typography variant="bodyBold">Email</Typography>
              </TableCell>
              <TableCell style={{ width: '%' }} align="left">
                <Typography variant="bodyBold">Wallets</Typography>
              </TableCell>
              <TableCell style={{ width: '20%' }} align="right">
                <Typography variant="bodyBold">Instructions</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companyProfiles.map((companyProfileCTO: CompanyProfileCTO) => {
              return (
                <AdminCompanyProfileRow
                  refreshCallBack={refreshCallBack}
                  key={companyProfileCTO.uuid}
                  companyProfileCTO={companyProfileCTO}
                  companyProfiles={companyProfiles}
                />
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}
